import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Plus, X, Save, ArrowUp, ArrowDown, GripVertical, Sparkles } from "lucide-react";
import { toast } from "sonner";
import CategoryManager from "@/components/CategoryManager";

const SETTING_GROUPS = [
  { key: "project_phases", title: "Proje Aşamaları", desc: "Proje takviminde faz seçerken görünür." },
  { key: "manufacturing_stages", title: "Ürün İmalat Aşamaları", desc: "Proje ürünlerinde imalat aşaması seçerken görünür." },
  { key: "worker_roles", title: "İşçi Rolleri", desc: "Kullanıcılara atanabilen roller (usta, çırak, kalfa, boyacı...). AI planlama asistanı bu rolleri kullanır." },
];

function Editor({ title, desc, values, onSave }) {
  const [list, setList] = useState(values || []);
  const [input, setInput] = useState("");
  const [dirty, setDirty] = useState(false);
  useEffect(() => { setList(values || []); setDirty(false); }, [values]);

  const slug = title.replace(/\s/g, "-").toLowerCase();

  const add = () => {
    const v = input.trim();
    if (!v) return;
    if (list.includes(v)) { toast.error("Zaten mevcut"); return; }
    setList([...list, v]);
    setInput("");
    setDirty(true);
  };
  const remove = (i) => { setList(list.filter((_, idx) => idx !== i)); setDirty(true); };
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    const next = list.slice();
    [next[i], next[j]] = [next[j], next[i]];
    setList(next);
    setDirty(true);
  };
  const save = async () => {
    await onSave(list);
    setDirty(false);
    toast.success("Kaydedildi");
  };

  return (
    <Card className="p-5 border border-zinc-200 shadow-none space-y-3" data-testid={`setting-card-${slug}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-medium">{title}</h3>
          <div className="text-sm text-zinc-500">{desc}</div>
        </div>
        {dirty && <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5 shrink-0">Kaydedilmemiş değişiklik</span>}
      </div>

      <ol className="rounded-md border border-zinc-200 divide-y divide-zinc-100 bg-white overflow-hidden" data-testid={`setting-list-${slug}`}>
        {list.length === 0 && (
          <li className="p-3 text-sm text-zinc-500 text-center">Henüz değer yok</li>
        )}
        {list.map((v, i) => (
          <li key={`${v}-${i}`} className="flex items-center gap-2 px-3 py-2" data-testid={`setting-row-${slug}-${i}`}>
            <GripVertical size={14} className="text-zinc-300 shrink-0" />
            <span className="text-xs text-zinc-400 w-6 tabular-nums">{i + 1}.</span>
            <span className="flex-1 text-sm">{v}</span>
            <button
              onClick={() => move(i, -1)}
              disabled={i === 0}
              className="p-1 rounded hover:bg-zinc-100 disabled:opacity-30 disabled:cursor-not-allowed"
              title="Yukarı taşı"
              data-testid={`setting-up-${slug}-${i}`}
            ><ArrowUp size={14} /></button>
            <button
              onClick={() => move(i, +1)}
              disabled={i === list.length - 1}
              className="p-1 rounded hover:bg-zinc-100 disabled:opacity-30 disabled:cursor-not-allowed"
              title="Aşağı taşı"
              data-testid={`setting-down-${slug}-${i}`}
            ><ArrowDown size={14} /></button>
            <button
              onClick={() => remove(i)}
              className="p-1 rounded hover:bg-red-50 text-zinc-400 hover:text-red-600"
              title="Sil"
              data-testid={`setting-remove-${slug}-${i}`}
            ><X size={14} /></button>
          </li>
        ))}
      </ol>

      <div className="flex gap-2">
        <Input
          data-testid={`setting-input-${slug}`}
          placeholder="Yeni değer ekle (Enter ile ekle)..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
        />
        <Button onClick={add} variant="outline" data-testid={`setting-add-${slug}`}><Plus size={14}/></Button>
        <Button onClick={save} disabled={!dirty} className="bg-zinc-900 hover:bg-zinc-800 disabled:opacity-40" data-testid={`setting-save-${slug}`}>
          <Save size={14} className="mr-1"/>Kaydet
        </Button>
      </div>
      <div className="text-xs text-zinc-400">
        İpucu: ↑ ve ↓ tuşları ile sırayı değiştirin. Yeni eklenen sona gelir; istediğiniz yere ok tuşlarıyla taşıyabilirsiniz. Sıra kaydettiğinizde tüm açılır menülerde bu sırayla görünür.
      </div>
    </Card>
  );
}

function QuickActionsManager() {
  const [list, setList] = useState([]);
  const [dirty, setDirty] = useState(false);
  const load = async () => {
    const { data } = await api.get("/ai/quick-actions");
    setList(data.actions || []);
    setDirty(false);
  };
  useEffect(() => { load(); }, []);
  const update = (i, field, val) => {
    setList(list.map((a, idx) => idx === i ? { ...a, [field]: val } : a));
    setDirty(true);
  };
  const add = () => { setList([...list, { label: "", prompt: "" }]); setDirty(true); };
  const remove = (i) => { setList(list.filter((_, idx) => idx !== i)); setDirty(true); };
  const save = async () => {
    const clean = list.filter((a) => a.label.trim() && a.prompt.trim());
    await api.put("/ai/quick-actions", { actions: clean });
    await load();
    toast.success("Hızlı komutlar kaydedildi");
  };
  return (
    <Card className="p-5 border border-zinc-200 shadow-none space-y-3" data-testid="ai-quick-actions-manager">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <div className="w-8 h-8 rounded-md bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center text-white shrink-0"><Sparkles size={16} /></div>
          <div>
            <h3 className="text-lg font-medium">AI Hızlı Komutlar</h3>
            <div className="text-sm text-zinc-500">Planlama asistanının sohbetinde buton olarak görünen hazır komutlar.</div>
          </div>
        </div>
        {dirty && <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5 shrink-0">Kaydedilmemiş değişiklik</span>}
      </div>
      <div className="space-y-2">
        {list.length === 0 && <div className="text-sm text-zinc-500 text-center py-3 border border-zinc-200 rounded-md">Henüz komut yok</div>}
        {list.map((a, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-start" data-testid={`ai-quick-row-${i}`}>
            <Input className="col-span-4" placeholder="Buton etiketi" value={a.label}
                   data-testid={`ai-quick-label-${i}`}
                   onChange={(e) => update(i, "label", e.target.value)} />
            <Input className="col-span-7" placeholder="Asistana gönderilecek komut..." value={a.prompt}
                   data-testid={`ai-quick-prompt-${i}`}
                   onChange={(e) => update(i, "prompt", e.target.value)} />
            <button onClick={() => remove(i)} className="col-span-1 p-2 rounded hover:bg-red-50 text-zinc-400 hover:text-red-600 justify-self-center"
                    data-testid={`ai-quick-remove-${i}`}><X size={14} /></button>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <Button onClick={add} variant="outline" data-testid="ai-quick-add"><Plus size={14} className="mr-1" />Komut Ekle</Button>
        <Button onClick={save} disabled={!dirty} className="bg-zinc-900 hover:bg-zinc-800 disabled:opacity-40" data-testid="ai-quick-save">
          <Save size={14} className="mr-1" />Kaydet
        </Button>
      </div>
    </Card>
  );
}

function WorkshopContextManager() {
  const [text, setText] = useState("");
  const [saved, setSaved] = useState("");
  const load = async () => {
    const { data } = await api.get("/ai/context");
    setText(data.text || "");
    setSaved(data.text || "");
  };
  useEffect(() => { load(); }, []);
  const dirty = text !== saved;
  const save = async () => {
    const { data } = await api.put("/ai/context", { text });
    setSaved(data.text || "");
    setText(data.text || "");
    toast.success("Atölye hafızası kaydedildi");
  };
  return (
    <Card className="p-5 border border-zinc-200 shadow-none space-y-3" data-testid="ai-context-manager">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <div className="w-8 h-8 rounded-md bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center text-white shrink-0"><Sparkles size={16} /></div>
          <div>
            <h3 className="text-lg font-medium">AI Atölye Hafızası</h3>
            <div className="text-sm text-zinc-500">İşyerinizin çalışma düzenini buraya yazın; asistan her planlamada bu bilgiyi dikkate alır.</div>
          </div>
        </div>
        {dirty && <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5 shrink-0">Kaydedilmemiş değişiklik</span>}
      </div>
      <Textarea
        data-testid="ai-context-input"
        rows={8}
        placeholder={"Örn: Bizde ayrı bir boyacı yoktur, boyayı ustalardan biri yapar. Kesim işleri sabah, montaj öğleden sonra yapılır. Cumartesi yarım gün çalışılır. Ali Usta ağırlıklı montaj, Veli Usta ağırlıklı kesim yapar..."}
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <div className="flex justify-end">
        <Button onClick={save} disabled={!dirty} className="bg-zinc-900 hover:bg-zinc-800 disabled:opacity-40" data-testid="ai-context-save">
          <Save size={14} className="mr-1" />Kaydet
        </Button>
      </div>
    </Card>
  );
}

export default function Settings() {
  const [settings, setSettings] = useState({});
  const load = async () => {
    const { data } = await api.get("/settings");
    setSettings(data);
  };
  useEffect(() => { load(); }, []);
  const save = async (key, values) => {
    await api.put(`/settings/${key}`, { values });
    load();
  };
  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-zinc-500">Ayarlar</div>
        <h1 className="text-3xl mt-1">Özel Girdiler</h1>
        <div className="text-sm text-zinc-500 mt-1">Uygulamada kullanılan açılır menü değerlerini burada yönet.</div>
      </div>
      <div className="space-y-4">
        {SETTING_GROUPS.map(g => (
          <Editor key={g.key} title={g.title} desc={g.desc} values={settings[g.key] || []} onSave={(v)=>save(g.key, v)} />
        ))}
      </div>

      <CategoryManager />
      <WorkshopContextManager />
      <QuickActionsManager />
    </div>
  );
}
